import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { Image } from 'expo-image';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { Fonts } from '@/constants/theme';
import {
  createOmrScanJobFromUri,
  getOmrJobStatus,
  isOmrApiConfigured,
  type LiveOmrScanResult,
} from '@/lib/omr-api';
import { createOmrResultBatch, persistOmrResultSheet } from '@/lib/omr-results';
import {
  fetchQuestionsForSet,
  fetchTeacherQuestionSets,
  type QuestionListItem,
  type TeacherQuestionSetListItem,
} from '@/lib/question-sets';
import { fetchStudentsForClass, type StudentListItem } from '@/lib/students-admin';
import { useAuth } from '@/providers/auth-provider';

type PreviewImage = {
  id: string;
  jobId?: string;
  name: string;
  result?: LiveOmrScanResult;
  saveState?: 'idle' | 'saving' | 'saved' | 'failed';
  saveStatusText?: string;
  sizeLabel: string;
  status: 'idle' | 'uploading' | 'queued' | 'processing' | 'success' | 'no_qr' | 'error';
  statusText: string;
  uri: string;
};

function formatFileSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return 'Image file';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export default function TeacherScanDesktopScreen() {
  const { profile } = useAuth();
  const previewUrisRef = useRef<string[]>([]);
  const [sets, setSets] = useState<TeacherQuestionSetListItem[]>([]);
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [loadingSets, setLoadingSets] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [processingImages, setProcessingImages] = useState(false);
  const [savingResults, setSavingResults] = useState(false);
  const [previewImages, setPreviewImages] = useState<PreviewImage[]>([]);
  const [questions, setQuestions] = useState<QuestionListItem[]>([]);
  const [selectedPreviewId, setSelectedPreviewId] = useState<string | null>(null);
  const [showOverlayImage, setShowOverlayImage] = useState(false);

  const selectedSet = useMemo(
    () => sets.find((item) => item.id === selectedSetId) ?? null,
    [selectedSetId, sets],
  );

  useEffect(() => {
    async function loadSets() {
      if (!profile?.id) {
        setLoadingSets(false);
        return;
      }

      try {
        const data = await fetchTeacherQuestionSets(profile.id);
        setSets(data);
        if (data.length > 0) {
          setSelectedSetId((current) => current ?? data[0].id);
        }
      } catch (error) {
        console.error('Failed to load teacher sets:', error);
      } finally {
        setLoadingSets(false);
      }
    }

    void loadSets();
  }, [profile?.id]);

  useEffect(() => {
    async function loadStudents() {
      if (!selectedSet?.class_id || !selectedSet.school_id) {
        setStudents([]);
        return;
      }

      setLoadingStudents(true);
      try {
        const data = await fetchStudentsForClass(selectedSet.class_id, selectedSet.school_id);
        setStudents(data);
      } catch (error) {
        console.error('Failed to load class students:', error);
        setStudents([]);
      } finally {
        setLoadingStudents(false);
      }
    }

    void loadStudents();
  }, [selectedSet?.class_id, selectedSet?.school_id]);

  useEffect(() => {
    async function loadQuestions() {
      if (!selectedSet?.id) {
        setQuestions([]);
        return;
      }

      setLoadingQuestions(true);
      try {
        const data = await fetchQuestionsForSet(selectedSet.id);
        setQuestions(data);
      } catch (error) {
        console.error('Failed to load set questions:', error);
        setQuestions([]);
      } finally {
        setLoadingQuestions(false);
      }
    }

    void loadQuestions();
  }, [selectedSet?.id]);

  useEffect(() => {
    return () => {
      for (const uri of previewUrisRef.current) {
        if (uri.startsWith('blob:')) {
          URL.revokeObjectURL(uri);
        }
      }
    };
  }, []);

  const pickImages = () => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;

    input.onchange = () => {
      const files = Array.from(input.files ?? []);
      const nextImages = files.map((file, index) => ({
        id: `${file.name}-${file.lastModified}-${index}`,
        jobId: undefined,
        name: file.name,
        result: undefined,
        saveState: 'idle' as const,
        saveStatusText: '',
        sizeLabel: formatFileSize(file.size),
        status: 'idle' as const,
        statusText: 'Ready to upload',
        uri: URL.createObjectURL(file),
      }));

      setPreviewImages((current) => {
        for (const image of current) {
          if (image.uri.startsWith('blob:')) {
            URL.revokeObjectURL(image.uri);
          }
        }
        previewUrisRef.current = nextImages.map((image) => image.uri);
        return nextImages;
      });
    };

    input.click();
  };

  const updatePreviewImage = (id: string, patch: Partial<PreviewImage>) => {
    setPreviewImages((current) =>
      current.map((image) => (image.id === id ? { ...image, ...patch } : image)),
    );
  };

  const processSelectedImages = async () => {
    if (!selectedSet) {
      Alert.alert('Select a set', 'Choose an MCQ set before uploading scan images.');
      return;
    }

    if (!selectedSet.question_count) {
      Alert.alert('No questions', 'This set has no questions yet, so scanning cannot start.');
      return;
    }

    if (selectedSet.question_count < 5) {
      Alert.alert(
        'Not enough questions',
        'OMR scanning requires at least 5 questions in the selected set.',
      );
      return;
    }

    if (!isOmrApiConfigured()) {
      Alert.alert(
        'OMR API missing',
        'Set EXPO_PUBLIC_OMR_API_URL so the desktop scan page can upload and process images.',
      );
      return;
    }

    if (previewImages.length === 0) {
      Alert.alert('No images', 'Add one or more images before starting the scan.');
      return;
    }

    setProcessingImages(true);

    try {
      for (const image of previewImages) {
        updatePreviewImage(image.id, {
          result: undefined,
          saveState: 'idle',
          saveStatusText: '',
          status: 'uploading',
          statusText: 'Uploading to backend',
        });

        try {
          const queued = await createOmrScanJobFromUri({
            imageUri: image.uri,
            schoolId: selectedSet.school_id,
            questionCount: selectedSet.question_count,
            batchId: selectedSet.id,
          });

          updatePreviewImage(image.id, {
            jobId: queued.jobId,
            status: 'queued',
            statusText: 'Queued for processing',
          });

          let finished = false;
          while (!finished) {
            const job = await getOmrJobStatus(queued.jobId);

            if (job.status === 'queued') {
              updatePreviewImage(image.id, {
                status: 'queued',
                statusText: 'Queued for processing',
              });
            } else if (job.status === 'processing') {
              updatePreviewImage(image.id, {
                status: 'processing',
                statusText: 'Analyzing sheet',
              });
            } else if (job.status === 'success' || job.status === 'no_qr') {
              const result = job.result ?? {
                status: job.status,
                message: job.message || 'Scan completed.',
              };

              updatePreviewImage(image.id, {
                result,
                saveState: 'idle',
                saveStatusText: 'Ready to save',
                status: job.status,
                statusText:
                  job.status === 'success'
                    ? result.evaluation
                      ? `${result.evaluation.score}/${result.evaluation.total} score`
                      : 'Scan complete'
                    : result.message || 'Review required',
              });
              finished = true;
            } else if (job.status === 'error' || job.status === 'failed' || job.status === 'not_found') {
              updatePreviewImage(image.id, {
                status: 'error',
                statusText: job.error || job.message || 'Scan failed',
              });
              finished = true;
            } else {
              updatePreviewImage(image.id, {
                status: 'processing',
                statusText: 'Waiting for backend',
              });
            }

            if (!finished) {
              await new Promise((resolve) => setTimeout(resolve, 1500));
            }
          }
        } catch (error) {
          updatePreviewImage(image.id, {
            status: 'error',
            statusText: error instanceof Error ? error.message : 'Scan failed',
          });
        }
      }
    } finally {
      setProcessingImages(false);
    }
  };

  const savablePreviewImages = useMemo(
    () =>
      previewImages.filter(
        (image) => Boolean(image.result) && (image.status === 'success' || image.status === 'no_qr'),
      ),
    [previewImages],
  );

  const unsavedPreviewCount = useMemo(
    () => savablePreviewImages.filter((image) => image.saveState !== 'saved').length,
    [savablePreviewImages],
  );

  const saveProcessedResults = async () => {
    if (!selectedSet) {
      Alert.alert('Select a set', 'Choose an MCQ set before saving scan results.');
      return;
    }

    if (!profile?.id) {
      Alert.alert('Profile missing', 'Your teacher profile is required before scan results can be saved.');
      return;
    }

    if (savablePreviewImages.length === 0) {
      Alert.alert('No processed results', 'Process one or more images before saving.');
      return;
    }

    setSavingResults(true);

    try {
      const scanSessionId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `scan-${Date.now()}`;

      const persistedBatch = await createOmrResultBatch({
        actorTeacherId: profile.id,
        backendJobId: scanSessionId,
        selectedSet,
        sheetCount: savablePreviewImages.length,
        totalStudents: students.length,
      });

      let savedCount = 0;
      let failedCount = 0;

      for (const image of savablePreviewImages) {
        if (!image.result) {
          continue;
        }

        updatePreviewImage(image.id, {
          saveState: 'saving',
          saveStatusText: 'Saving result',
        });

        try {
          await persistOmrResultSheet({
            batchId: persistedBatch.id,
            questions,
            result: image.result,
            schoolId: selectedSet.school_id,
            sourceFilename: image.name,
            students,
          });

          savedCount += 1;
          updatePreviewImage(image.id, {
            saveState: 'saved',
            saveStatusText: 'Saved to records',
          });
        } catch (error) {
          failedCount += 1;
          console.error('Failed to persist OMR sheet result:', error);
          updatePreviewImage(image.id, {
            saveState: 'failed',
            saveStatusText: error instanceof Error ? error.message : 'Save failed',
          });
        }
      }

      Alert.alert(
        'Save complete',
        failedCount > 0
          ? `${savedCount} result(s) saved and ${failedCount} failed.`
          : `${savedCount} result(s) saved successfully.`,
      );
    } catch (error) {
      Alert.alert(
        'Save failed',
        error instanceof Error ? error.message : 'Unable to save processed results.',
      );
    } finally {
      setSavingResults(false);
    }
  };

  const studentSummary = loadingStudents
    ? 'Loading students...'
    : selectedSet
      ? `${students.length} students in ${selectedSet.class_label}`
      : 'Select an MCQ set to load students';

  const questionLookup = useMemo(
    () => new Map(questions.map((question) => [question.position, question])),
    [questions],
  );

  const selectedPreview = useMemo(
    () => previewImages.find((image) => image.id === selectedPreviewId) ?? null,
    [previewImages, selectedPreviewId],
  );

  const selectedPreviewImageSource = useMemo(() => {
    if (showOverlayImage && selectedPreview?.result?.debug?.bubble_overlay_url) {
      return selectedPreview.result.debug.bubble_overlay_url;
    }
    return selectedPreview?.uri ?? null;
  }, [selectedPreview, showOverlayImage]);

  const selectedPreviewStudent = useMemo(() => {
    const backendRoll = selectedPreview?.result?.student?.roll?.trim();
    if (!backendRoll) {
      return null;
    }

    const normalizedBackendRoll = backendRoll.toLowerCase();
    return (
      students.find((student) => student.roll_number.trim().toLowerCase() === normalizedBackendRoll) ??
      null
    );
  }, [selectedPreview?.result?.student?.roll, students]);

  const selectedPreviewStudentSummary = useMemo(() => {
    const backendStudent = selectedPreview?.result?.student;
    if (!backendStudent && !selectedPreviewStudent) {
      return null;
    }

    return {
      classLabel: selectedPreviewStudent?.class_label ?? selectedSet?.class_label ?? 'Class unavailable',
      name:
        selectedPreviewStudent?.full_name?.trim() ||
        backendStudent?.name?.trim() ||
        (backendStudent?.roll ? `Student ${backendStudent.roll}` : 'Student'),
      roll: selectedPreviewStudent?.roll_number ?? backendStudent?.roll ?? null,
    };
  }, [selectedPreview?.result?.student, selectedPreviewStudent, selectedSet?.class_label]);

  const selectedPreviewAnalysis = useMemo(() => {
    const answers = selectedPreview?.result?.omr?.questions ?? [];
    let correctCount = 0;
    let incorrectCount = 0;
    let blankCount = 0;

    const rows = answers.map((answer) => {
      const question = questionLookup.get(answer.question_number);
      const marked = answer.selected_option ?? '-';
      const correct = question?.correct_option ?? '-';
      const verdict =
        marked === '-' || answer.status === 'blank'
          ? 'blank'
          : marked === correct
            ? 'correct'
            : 'incorrect';

      if (verdict === 'correct') correctCount += 1;
      if (verdict === 'incorrect') incorrectCount += 1;
      if (verdict === 'blank') blankCount += 1;

      return {
        questionNumber: answer.question_number,
        marked,
        correct,
        verdict,
      };
    });

    return {
      rows,
      correctCount,
      incorrectCount,
      blankCount,
    };
  }, [questionLookup, selectedPreview]);

  return (
    <View style={styles.screen}>
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text style={styles.eyebrow}>Teacher scan</Text>
          <Text style={styles.title}>Batch upload workspace</Text>
          <Text style={styles.subtitle}>
            Select a set, confirm the class load, then add sheet images for preview.
          </Text>
        </View>
        <View style={styles.headerMeta}>
          <Text style={styles.metaLabel}>Selected class load</Text>
          <Text style={styles.metaValue}>{studentSummary}</Text>
        </View>
      </View>

      <View style={styles.layout}>
          <View style={styles.setRail}>
          <View style={styles.sectionHeader}>
            <Text style={styles.railSectionLabel}>MCQ sets</Text>
            <Text style={styles.railSectionHint}>
              {loadingSets ? 'Loading...' : `${sets.length} available`}
            </Text>
          </View>

          {loadingSets ? (
            <View style={styles.railState}>
              <ActivityIndicator color="#163828" />
              <Text style={styles.railStateText}>Loading sets</Text>
            </View>
          ) : sets.length === 0 ? (
            <View style={styles.railState}>
              <MaterialIcons name="auto-stories" size={24} color="#7C8A80" />
              <Text style={styles.railStateText}>No sets found</Text>
            </View>
          ) : (
            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.setList}>
              {sets.map((set) => {
                const isActive = set.id === selectedSetId;
                return (
                  <Pressable
                    key={set.id}
                    onPress={() => setSelectedSetId(set.id)}
                    style={({ pressed }) => [
                      styles.setItem,
                      isActive && styles.setItemActive,
                      pressed && styles.setItemPressed,
                    ]}>
                    <View style={styles.setItemTop}>
                      <Text style={[styles.setTitle, isActive && styles.setTitleActive]} numberOfLines={2}>
                        {set.title}
                      </Text>
                      {isActive ? <View style={styles.setActiveDot} /> : null}
                    </View>
                    <Text style={[styles.setMeta, isActive && styles.setMetaActive]} numberOfLines={1}>
                      {set.class_label}
                    </Text>
                    <Text style={[styles.setMeta, isActive && styles.setMetaActive]} numberOfLines={1}>
                      {set.subject_label}
                    </Text>
                    <Text style={[styles.setCount, isActive && styles.setCountActive]}>
                      {set.question_count} questions
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          )}
        </View>

        <View style={styles.workspace}>
          <View style={styles.workspaceTop}>
            <View style={styles.workspaceTitleWrap}>
              <Text style={styles.workspaceLabel}>Selected set</Text>
              <Text style={styles.workspaceTitle}>
                {selectedSet?.title ?? 'Choose a set to begin'}
              </Text>
              <Text style={styles.workspaceSub}>
                {selectedSet
                  ? `${selectedSet.class_label} | ${selectedSet.subject_label}`
                  : 'The upload area becomes active after a set is selected.'}
              </Text>
            </View>

            <View style={styles.workspaceStats}>
              <View style={styles.statBlock}>
                <Text style={styles.statValue}>
                  {loadingStudents ? '...' : selectedSet ? students.length : 0}
                </Text>
                <Text style={styles.statLabel}>Students loaded</Text>
              </View>
              <View style={styles.statBlock}>
                <Text style={styles.statValue}>
                  {loadingQuestions ? '...' : questions.length}
                </Text>
                <Text style={styles.statLabel}>Questions loaded</Text>
              </View>
            </View>
          </View>

          <Pressable
            onPress={pickImages}
            disabled={!selectedSet}
            style={({ pressed }) => [
              styles.uploadZone,
              !selectedSet && styles.uploadZoneDisabled,
              pressed && selectedSet && styles.uploadZonePressed,
            ]}>
            <View style={styles.uploadIconWrap}>
              <MaterialIcons
                name={previewImages.length > 0 ? 'imagesearch-roller' : 'upload-file'}
                size={34}
                color="#163828"
              />
            </View>
            <Text style={styles.uploadTitle}>
              {previewImages.length > 0 ? 'Add more or replace images' : 'Choose scan images'}
            </Text>
            <Text style={styles.uploadBody}>
              {selectedSet
                ? 'Open the file dialog and select one or more sheet images for preview.'
                : 'Select an MCQ set first to activate image upload.'}
            </Text>
          </Pressable>

          <View style={styles.actionRow}>
            <View style={styles.apiNote}>
              <Text style={styles.apiNoteText}>
                {!isOmrApiConfigured()
                  ? 'OMR backend is not configured yet.'
                  : savingResults
                    ? 'Saving processed results to Supabase.'
                    : unsavedPreviewCount > 0
                      ? `${unsavedPreviewCount} processed result(s) are ready to save.`
                      : 'Connected to the queued OMR backend pipeline.'}
              </Text>
            </View>
            <View style={styles.actionButtons}>
              <Pressable
                onPress={() => void saveProcessedResults()}
                disabled={processingImages || savingResults || unsavedPreviewCount === 0}
                style={({ pressed }) => [
                  styles.secondaryButton,
                  (processingImages || savingResults || unsavedPreviewCount === 0) &&
                    styles.processButtonDisabled,
                  pressed &&
                    !processingImages &&
                    !savingResults &&
                    unsavedPreviewCount > 0 &&
                    styles.processButtonPressed,
                ]}>
                {savingResults ? (
                  <ActivityIndicator color="#163828" />
                ) : (
                  <Text style={styles.secondaryButtonText}>Save results</Text>
                )}
              </Pressable>
              <Pressable
                onPress={() => void processSelectedImages()}
                disabled={!selectedSet || previewImages.length === 0 || processingImages || savingResults || !isOmrApiConfigured()}
                style={({ pressed }) => [
                  styles.processButton,
                  (!selectedSet || previewImages.length === 0 || processingImages || savingResults || !isOmrApiConfigured()) &&
                    styles.processButtonDisabled,
                  pressed &&
                    selectedSet &&
                    previewImages.length > 0 &&
                    !processingImages &&
                    !savingResults &&
                    isOmrApiConfigured() &&
                    styles.processButtonPressed,
                ]}>
                {processingImages ? (
                  <ActivityIndicator color="#F7F4EE" />
                ) : (
                  <Text style={styles.processButtonText}>Process images</Text>
                )}
              </Pressable>
            </View>
          </View>

          <View style={styles.previewHeader}>
            <Text style={styles.sectionLabel}>Image preview</Text>
            <Text style={styles.sectionHint}>
              {previewImages.length > 0 ? `${previewImages.length} files ready` : 'No images selected'}
            </Text>
          </View>

          {previewImages.length === 0 ? (
            <View style={styles.emptyPreview}>
              <MaterialIcons name="photo-library" size={28} color="#7C8A80" />
              <Text style={styles.emptyPreviewTitle}>Preview tiles appear here</Text>
              <Text style={styles.emptyPreviewBody}>
                Selected scan images will be shown as small previews.
              </Text>
            </View>
          ) : (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.previewList}>
              {previewImages.map((image) => (
                <Pressable
                  key={image.id}
                  onPress={() => {
                    setSelectedPreviewId(image.id);
                    setShowOverlayImage(false);
                  }}
                  style={({ pressed }) => [
                    styles.previewCard,
                    pressed && styles.previewCardPressed,
                  ]}>
                  <View style={styles.previewImageWrap}>
                    <Image
                      source={{ uri: image.uri }}
                      style={[
                        styles.previewImage,
                        image.status !== 'success' && image.status !== 'no_qr' && styles.previewImagePending,
                      ]}
                      contentFit="cover"
                      blurRadius={image.status !== 'success' && image.status !== 'no_qr' ? 8 : 0}
                    />
                    {image.status !== 'idle' ? (
                      <View
                        style={[
                          styles.previewStateOverlay,
                          image.status === 'success' && styles.previewStateOverlayDone,
                          image.status === 'no_qr' && styles.previewStateOverlayPending,
                          image.status === 'error' && styles.previewStateOverlayError,
                        ]}>
                        {image.status === 'success' ? (
                          <View style={styles.previewStateBadge}>
                            <MaterialIcons name="check-circle" size={18} color="#F7F4EE" />
                            <Text style={styles.previewStateBadgeText}>Done</Text>
                          </View>
                        ) : image.status === 'error' ? (
                          <View style={styles.previewStateBadgeError}>
                            <MaterialIcons name="error-outline" size={18} color="#FFF3ED" />
                            <Text style={styles.previewStateBadgeText}>Failed</Text>
                          </View>
                        ) : (
                          <View style={styles.previewStateLoader}>
                            <ActivityIndicator color="#F7F4EE" />
                            <Text style={styles.previewStateLoaderText}>{image.statusText}</Text>
                          </View>
                        )}
                      </View>
                    ) : null}
                  </View>
                  <View style={styles.previewMeta}>
                    <View style={styles.previewStatusRow}>
                      <Text
                        style={[
                          styles.previewStatus,
                          image.status === 'error' && styles.previewStatusError,
                          image.status === 'success' && styles.previewStatusSuccess,
                        ]}>
                        {image.statusText}
                      </Text>
                    </View>
                    <Text style={styles.previewName} numberOfLines={1}>
                      {image.name}
                    </Text>
                    <Text style={styles.previewSize}>{image.sizeLabel}</Text>
                    {image.result?.student?.roll ? (
                      <Text style={styles.previewResult}>Roll {image.result.student.roll}</Text>
                    ) : null}
                    {image.saveStatusText ? (
                      <Text
                        style={[
                          styles.previewResult,
                          image.saveState === 'saved' && styles.previewResultSaved,
                          image.saveState === 'failed' && styles.previewResultFailed,
                        ]}>
                        {image.saveStatusText}
                      </Text>
                    ) : null}
                  </View>
                </Pressable>
              ))}
            </ScrollView>
          )}
        </View>
      </View>

      <Modal
        animationType="fade"
        transparent
        visible={Boolean(selectedPreview)}
        onRequestClose={() => {
          setSelectedPreviewId(null);
          setShowOverlayImage(false);
        }}>
        <View style={styles.modalBackdrop}>
          <Pressable
            style={styles.modalScrim}
            onPress={() => {
              setSelectedPreviewId(null);
              setShowOverlayImage(false);
            }}
          />
          {selectedPreview ? (
            <View style={styles.modalCard}>
              <View style={styles.modalHeader}>
                <View style={styles.modalHeaderText}>
                  <Text style={styles.modalEyebrow}>OMR sheet</Text>
                  <Text style={styles.modalTitle} numberOfLines={1}>
                    {selectedPreviewStudentSummary?.name ?? selectedPreview.name}
                  </Text>
                  <Text style={styles.modalSubtitle}>
                    {selectedPreviewStudentSummary?.classLabel
                      ? `${selectedPreviewStudentSummary.classLabel} • ${selectedPreview.statusText}`
                      : selectedPreview.statusText}
                  </Text>
                </View>
                <View style={styles.modalActions}>
                  {selectedPreview.result?.debug?.bubble_overlay_url ? (
                    <Pressable
                      style={[styles.modalIconButton, showOverlayImage && styles.modalIconButtonActive]}
                      onPress={() => setShowOverlayImage((current) => !current)}>
                      <MaterialIcons
                        name={showOverlayImage ? 'visibility-off' : 'visibility'}
                        size={20}
                        color={showOverlayImage ? '#F7F4EE' : '#163828'}
                      />
                    </Pressable>
                  ) : null}
                  <Pressable
                    style={styles.modalClose}
                    onPress={() => {
                      setSelectedPreviewId(null);
                      setShowOverlayImage(false);
                    }}>
                    <MaterialIcons name="close" size={20} color="#163828" />
                  </Pressable>
                </View>
              </View>

              <View style={styles.modalBody}>
                <View style={styles.modalImagePanel}>
                  <Image
                    source={selectedPreviewImageSource ? { uri: selectedPreviewImageSource } : undefined}
                    style={styles.modalImage}
                    contentFit="contain"
                  />
                  {selectedPreviewStudentSummary ? (
                    <View style={styles.studentInfoCard}>
                      <View style={styles.studentInfoRow}>
                        <View style={styles.studentInfoBlock}>
                          <Text style={styles.studentInfoLabel}>Student name</Text>
                          <Text style={styles.studentInfoValue}>{selectedPreviewStudentSummary.name}</Text>
                        </View>
                        <View style={styles.studentInfoBlockCompact}>
                          <Text style={styles.studentInfoLabel}>Roll</Text>
                          <Text style={styles.studentInfoValue}>
                            {selectedPreviewStudentSummary.roll ?? 'Unavailable'}
                          </Text>
                        </View>
                      </View>
                      <View style={styles.studentInfoRow}>
                        <View style={styles.studentInfoBlock}>
                          <Text style={styles.studentInfoLabel}>Class</Text>
                          <Text style={styles.studentInfoValue}>{selectedPreviewStudentSummary.classLabel}</Text>
                        </View>
                      </View>
                    </View>
                  ) : null}
                  <View style={styles.modalMetaRow}>
                    <Text style={styles.modalMetaText}>{selectedPreview.sizeLabel}</Text>
                    <Text style={styles.modalMetaText}>
                      {showOverlayImage ? 'Bubble overlay view' : 'Original sheet view'}
                    </Text>
                    <Text style={styles.modalMetaText}>{selectedPreview.status}</Text>
                    {selectedPreviewStudentSummary?.roll ? (
                      <Text style={styles.modalMetaText}>
                        Roll {selectedPreviewStudentSummary.roll}
                      </Text>
                    ) : null}
                    {selectedPreview.result?.evaluation ? (
                      <Text style={styles.modalMetaText}>
                        Score {selectedPreview.result.evaluation.score}/{selectedPreview.result.evaluation.total}
                      </Text>
                    ) : null}
                  </View>
                </View>

                <View style={styles.modalAnswerPanel}>
                  <View style={styles.answerSummary}>
                    <View style={styles.answerSummaryCard}>
                      <Text style={styles.answerSummaryValue}>{selectedPreviewAnalysis.correctCount}</Text>
                      <Text style={styles.answerSummaryLabel}>Correct</Text>
                    </View>
                    <View style={styles.answerSummaryCard}>
                      <Text style={styles.answerSummaryValue}>{selectedPreviewAnalysis.incorrectCount}</Text>
                      <Text style={styles.answerSummaryLabel}>Incorrect</Text>
                    </View>
                    <View style={styles.answerSummaryCard}>
                      <Text style={styles.answerSummaryValue}>{selectedPreviewAnalysis.blankCount}</Text>
                      <Text style={styles.answerSummaryLabel}>Blank</Text>
                    </View>
                  </View>

                  <View style={styles.answerPanelHead}>
                    <View>
                      <Text style={styles.answerPanelTitle}>Answer review</Text>
                      <Text style={styles.answerPanelSub}>Marked option matched against the set answer key.</Text>
                    </View>
                    <View style={styles.answerLegend}>
                      <Text style={[styles.answerLegendItem, styles.answerMatchYes]}>Correct</Text>
                      <Text style={[styles.answerLegendItem, styles.answerMatchNo]}>Incorrect</Text>
                      <Text style={[styles.answerLegendItem, styles.answerMatchBlank]}>Blank</Text>
                    </View>
                  </View>

                  {selectedPreviewAnalysis.rows.length ? (
                    <View style={styles.answerTable}>
                      <View style={styles.answerTableHead}>
                        <Text style={styles.answerTableHeadTextQuestion}>Q</Text>
                        <Text style={styles.answerTableHeadText}>Marked</Text>
                        <Text style={styles.answerTableHeadText}>Correct</Text>
                        <Text style={styles.answerTableHeadTextState}>State</Text>
                      </View>
                      <ScrollView showsVerticalScrollIndicator={false} style={styles.answerList}>
                        {selectedPreviewAnalysis.rows.map((answer) => (
                          <View key={`${selectedPreview.id}-${answer.questionNumber}`} style={styles.answerRow}>
                            <Text style={styles.answerQuestion}>Q{answer.questionNumber}</Text>
                            <Text style={styles.answerMarked}>{answer.marked}</Text>
                            <Text style={styles.answerCorrect}>{answer.correct}</Text>
                            <View
                              style={[
                                styles.answerBadge,
                                answer.verdict === 'correct' && styles.answerBadgeYes,
                                answer.verdict === 'incorrect' && styles.answerBadgeNo,
                                answer.verdict === 'blank' && styles.answerBadgeBlank,
                              ]}>
                              <Text
                                style={[
                                  styles.answerBadgeText,
                                  answer.verdict === 'correct' && styles.answerMatchYes,
                                  answer.verdict === 'incorrect' && styles.answerMatchNo,
                                  answer.verdict === 'blank' && styles.answerMatchBlank,
                                ]}>
                                {answer.verdict}
                              </Text>
                            </View>
                          </View>
                        ))}
                      </ScrollView>
                    </View>
                  ) : (
                    <View style={styles.modalEmptyState}>
                      <Text style={styles.modalEmptyText}>
                        No answer breakdown is available for this sheet yet.
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            </View>
          ) : null}
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: '#F4EFE6',
    paddingHorizontal: 28,
    paddingVertical: 24,
    gap: 22,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 24,
  },
  headerText: {
    flex: 1,
    gap: 8,
    maxWidth: 680,
  },
  eyebrow: {
    color: '#6C7B71',
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  title: {
    color: '#163828',
    fontSize: 34,
    lineHeight: 40,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
    letterSpacing: -0.8,
  },
  subtitle: {
    color: '#5D6E63',
    fontSize: 15,
    lineHeight: 22,
  },
  headerMeta: {
    minWidth: 240,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderRadius: 18,
    backgroundColor: '#FBF8F2',
    borderWidth: 1,
    borderColor: '#E4DCCF',
    gap: 4,
  },
  metaLabel: {
    color: '#7C8A80',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  metaValue: {
    color: '#163828',
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '700',
  },
  layout: {
    flex: 1,
    minHeight: 0,
    flexDirection: 'row',
    gap: 22,
  },
  setRail: {
    width: 320,
    borderRadius: 30,
    backgroundColor: '#103427',
    padding: 18,
    gap: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 10,
  },
  sectionLabel: {
    color: '#163828',
    fontSize: 12,
    fontWeight: '900',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
  },
  sectionHint: {
    color: '#819086',
    fontSize: 12,
    fontWeight: '600',
  },
  railSectionLabel: {
    color: '#D7E5DC',
    fontSize: 12,
    fontWeight: '900',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
  },
  railSectionHint: {
    color: '#9CB0A2',
    fontSize: 12,
    fontWeight: '600',
  },
  railState: {
    flex: 1,
    minHeight: 180,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  railStateText: {
    color: '#D3DED7',
    fontSize: 13,
    fontWeight: '600',
  },
  setList: {
    gap: 10,
  },
  setItem: {
    borderRadius: 22,
    paddingHorizontal: 14,
    paddingVertical: 14,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    gap: 6,
  },
  setItemActive: {
    backgroundColor: '#EAF3E7',
    borderColor: '#DCE9D8',
  },
  setItemPressed: {
    opacity: 0.92,
  },
  setItemTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  setTitle: {
    flex: 1,
    color: '#F2F6F2',
    fontSize: 15,
    lineHeight: 20,
    fontWeight: '800',
  },
  setTitleActive: {
    color: '#163828',
  },
  setActiveDot: {
    width: 9,
    height: 9,
    borderRadius: 999,
    backgroundColor: '#163828',
    marginTop: 5,
  },
  setMeta: {
    color: '#AAC0B2',
    fontSize: 12,
    lineHeight: 17,
  },
  setMetaActive: {
    color: '#536556',
  },
  setCount: {
    color: '#F0E1BE',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    marginTop: 4,
  },
  setCountActive: {
    color: '#6B7C71',
  },
  workspace: {
    flex: 1,
    minWidth: 0,
    borderRadius: 34,
    backgroundColor: '#FBF8F2',
    borderWidth: 1,
    borderColor: '#E4DCCF',
    padding: 24,
    gap: 18,
  },
  workspaceTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 18,
  },
  workspaceTitleWrap: {
    flex: 1,
    gap: 6,
  },
  workspaceLabel: {
    color: '#6C7B71',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  workspaceTitle: {
    color: '#163828',
    fontSize: 28,
    lineHeight: 34,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
    letterSpacing: -0.6,
  },
  workspaceSub: {
    color: '#66776C',
    fontSize: 14,
    lineHeight: 21,
  },
  workspaceStats: {
    flexDirection: 'row',
    gap: 12,
  },
  statBlock: {
    minWidth: 118,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 18,
    backgroundColor: '#F2ECE1',
    gap: 2,
  },
  statValue: {
    color: '#163828',
    fontSize: 26,
    lineHeight: 30,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  statLabel: {
    color: '#6E7E73',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  uploadZone: {
    minHeight: 220,
    borderRadius: 28,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: '#CBBEA8',
    backgroundColor: '#F3EBDD',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    paddingHorizontal: 24,
    paddingVertical: 28,
  },
  uploadZoneDisabled: {
    opacity: 0.55,
  },
  uploadZonePressed: {
    transform: [{ scale: 0.995 }],
  },
  uploadIconWrap: {
    width: 76,
    height: 76,
    borderRadius: 24,
    backgroundColor: '#E7EFE8',
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadTitle: {
    color: '#163828',
    fontSize: 21,
    lineHeight: 26,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
    textAlign: 'center',
  },
  uploadBody: {
    maxWidth: 460,
    color: '#66776C',
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'center',
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  apiNote: {
    flex: 1,
    minHeight: 52,
    borderRadius: 18,
    backgroundColor: '#F2ECE1',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  apiNoteText: {
    color: '#5E7065',
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '600',
  },
  processButton: {
    minWidth: 180,
    minHeight: 52,
    borderRadius: 18,
    backgroundColor: '#163828',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  processButtonDisabled: {
    opacity: 0.45,
  },
  processButtonPressed: {
    transform: [{ scale: 0.99 }],
  },
  secondaryButton: {
    minWidth: 168,
    minHeight: 52,
    borderRadius: 18,
    backgroundColor: '#E6EEE8',
    borderWidth: 1,
    borderColor: '#C8D5CA',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  secondaryButtonText: {
    color: '#163828',
    fontSize: 15,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  processButtonText: {
    color: '#F7F4EE',
    fontSize: 15,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  previewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 10,
  },
  emptyPreview: {
    flex: 1,
    minHeight: 160,
    borderRadius: 24,
    backgroundColor: '#F5F0E7',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 20,
  },
  emptyPreviewTitle: {
    color: '#163828',
    fontSize: 16,
    fontWeight: '800',
  },
  emptyPreviewBody: {
    color: '#738278',
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'center',
  },
  previewList: {
    gap: 14,
    paddingBottom: 4,
  },
  previewCard: {
    width: 292,
    borderRadius: 22,
    overflow: 'hidden',
    backgroundColor: '#F2ECE1',
    borderWidth: 1,
    borderColor: '#E1D7C8',
  },
  previewCardPressed: {
    opacity: 0.94,
    transform: [{ scale: 0.99 }],
  },
  previewImageWrap: {
    position: 'relative',
  },
  previewImage: {
    width: '100%',
    height: 140,
    backgroundColor: '#E3DDD1',
  },
  previewImagePending: {
    opacity: 0.55,
  },
  previewStateOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(14, 33, 24, 0.36)',
    padding: 14,
  },
  previewStateOverlayPending: {
    backgroundColor: 'rgba(14, 33, 24, 0.4)',
  },
  previewStateOverlayDone: {
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    backgroundColor: 'rgba(22, 56, 40, 0.14)',
    padding: 12,
  },
  previewStateOverlayError: {
    backgroundColor: 'rgba(104, 37, 21, 0.34)',
  },
  previewStateLoader: {
    minWidth: 150,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderRadius: 18,
    backgroundColor: 'rgba(10, 25, 18, 0.64)',
  },
  previewStateLoaderText: {
    color: '#F7F4EE',
    fontSize: 11,
    lineHeight: 15,
    fontWeight: '800',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  previewStateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderRadius: 999,
    backgroundColor: '#2C6B47',
    paddingHorizontal: 10,
    paddingVertical: 7,
  },
  previewStateBadgeError: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(131, 53, 35, 0.92)',
    paddingHorizontal: 10,
    paddingVertical: 7,
  },
  previewStateBadgeText: {
    color: '#F7F4EE',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  previewMeta: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    gap: 4,
  },
  previewStatusRow: {
    minHeight: 18,
  },
  previewStatus: {
    color: '#6B7C71',
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  previewStatusError: {
    color: '#A34C36',
  },
  previewStatusSuccess: {
    color: '#2C6B47',
  },
  previewName: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '700',
  },
  previewSize: {
    color: '#738278',
    fontSize: 12,
  },
  previewResult: {
    color: '#163828',
    fontSize: 12,
    fontWeight: '700',
  },
  previewResultSaved: {
    color: '#2C6B47',
  },
  previewResultFailed: {
    color: '#A34C36',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(10, 20, 15, 0.48)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 28,
  },
  modalScrim: {
    ...StyleSheet.absoluteFillObject,
  },
  modalCard: {
    width: '100%',
    maxWidth: 1260,
    maxHeight: '92%',
    borderRadius: 30,
    backgroundColor: '#FBF8F2',
    borderWidth: 1,
    borderColor: '#E4DCCF',
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    paddingHorizontal: 22,
    paddingVertical: 18,
    borderBottomWidth: 1,
    borderBottomColor: '#E8DED0',
  },
  modalHeaderText: {
    flex: 1,
    gap: 4,
  },
  modalEyebrow: {
    color: '#6C7B71',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  modalTitle: {
    color: '#163828',
    fontSize: 24,
    lineHeight: 30,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  modalSubtitle: {
    color: '#66776C',
    fontSize: 14,
    lineHeight: 20,
  },
  modalClose: {
    width: 40,
    height: 40,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F2ECE1',
  },
  modalActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  modalIconButton: {
    width: 40,
    height: 40,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F2ECE1',
  },
  modalIconButtonActive: {
    backgroundColor: '#163828',
  },
  modalBody: {
    flexDirection: 'row',
    gap: 0,
    minHeight: 0,
    flex: 1,
  },
  modalImagePanel: {
    flex: 1.05,
    minHeight: 0,
    padding: 20,
    gap: 12,
    borderRightWidth: 1,
    borderRightColor: '#E8DED0',
    backgroundColor: '#F7F1E7',
  },
  modalImage: {
    flex: 1,
    minHeight: 320,
    borderRadius: 22,
    backgroundColor: '#ECE3D5',
  },
  modalMetaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  studentInfoCard: {
    borderRadius: 22,
    backgroundColor: '#F3EBDD',
    borderWidth: 1,
    borderColor: '#E2D5C0',
    paddingHorizontal: 16,
    paddingVertical: 14,
    gap: 10,
  },
  studentInfoRow: {
    flexDirection: 'row',
    gap: 12,
  },
  studentInfoBlock: {
    flex: 1,
    gap: 4,
  },
  studentInfoBlockCompact: {
    minWidth: 132,
    gap: 4,
  },
  studentInfoLabel: {
    color: '#6B7C71',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  studentInfoValue: {
    color: '#163828',
    fontSize: 16,
    lineHeight: 21,
    fontWeight: '800',
  },
  modalMetaText: {
    color: '#5E7065',
    fontSize: 12,
    fontWeight: '700',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: '#EFE7DA',
  },
  modalAnswerPanel: {
    flex: 0.95,
    minWidth: 0,
    minHeight: 0,
    paddingHorizontal: 18,
    paddingVertical: 18,
    gap: 14,
  },
  answerSummary: {
    flexDirection: 'row',
    gap: 10,
  },
  answerSummaryCard: {
    flex: 1,
    borderRadius: 18,
    backgroundColor: '#F3ECDD',
    paddingHorizontal: 14,
    paddingVertical: 14,
    gap: 2,
  },
  answerSummaryValue: {
    color: '#163828',
    fontSize: 24,
    lineHeight: 28,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  answerSummaryLabel: {
    color: '#6A7B70',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  answerPanelHead: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  answerPanelTitle: {
    color: '#163828',
    fontSize: 14,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  answerPanelSub: {
    color: '#6C7B71',
    fontSize: 13,
    lineHeight: 18,
    marginTop: 4,
  },
  answerLegend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    justifyContent: 'flex-end',
  },
  answerLegendItem: {
    fontSize: 12,
    fontWeight: '800',
  },
  answerTable: {
    flex: 1,
    minHeight: 0,
    borderRadius: 20,
    backgroundColor: '#F7F1E7',
    borderWidth: 1,
    borderColor: '#E6DCCD',
    overflow: 'hidden',
  },
  answerTableHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: '#EFE5D7',
    borderBottomWidth: 1,
    borderBottomColor: '#E1D6C7',
  },
  answerTableHeadTextQuestion: {
    width: 44,
    color: '#6C7B71',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  answerTableHeadText: {
    flex: 1,
    color: '#6C7B71',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  answerTableHeadTextState: {
    width: 78,
    textAlign: 'right',
    color: '#6C7B71',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  answerList: {
    flex: 1,
  },
  answerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#E8DED0',
  },
  answerQuestion: {
    width: 44,
    color: '#163828',
    fontSize: 12,
    fontWeight: '800',
  },
  answerMarked: {
    flex: 1,
    color: '#405247',
    fontSize: 13,
    fontWeight: '700',
  },
  answerCorrect: {
    flex: 1,
    color: '#405247',
    fontSize: 13,
    fontWeight: '700',
  },
  answerBadge: {
    minWidth: 78,
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 999,
    alignItems: 'center',
  },
  answerBadgeYes: {
    backgroundColor: '#E4F1E8',
  },
  answerBadgeNo: {
    backgroundColor: '#F8E4DE',
  },
  answerBadgeBlank: {
    backgroundColor: '#F3EBD8',
  },
  answerBadgeText: {
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  answerMatch: {
    width: 36,
    fontSize: 11,
    fontWeight: '800',
    textAlign: 'right',
    textTransform: 'uppercase',
  },
  answerMatchYes: {
    color: '#2C6B47',
  },
  answerMatchNo: {
    color: '#A34C36',
  },
  answerMatchBlank: {
    color: '#8A7A57',
  },
  modalEmptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modalEmptyText: {
    color: '#738278',
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
  },
});
